import React from 'react';

export interface MessageFormatterProps {
  /** The text to format */
  text: string;
  /** Custom styling for hashtags */
  hashtagClassName?: string;
  /** Custom styling for mentions */
  mentionClassName?: string;
  /** Custom styling for links */
  linkClassName?: string;
  /** Callback when hashtag is clicked */
  onHashtagClick?: (hashtag: string) => void;
  /** Callback when mention is clicked */
  onMentionClick?: (username: string) => void;
  /** Callback when link is clicked */
  onLinkClick?: (url: string) => void;
  /** Whether to open links in new tab */
  openLinksInNewTab?: boolean;
  /** Maximum length to display for URLs */
  urlDisplayLength?: number;
}

export const MessageFormatter: React.FC<MessageFormatterProps> = ({
  text,
  hashtagClassName = 'text-blue-500 font-medium cursor-pointer hover:underline',
  mentionClassName = 'text-blue-500 font-medium cursor-pointer hover:underline',
  linkClassName = 'text-blue-500 underline hover:text-blue-400',
  onHashtagClick,
  onMentionClick,
  onLinkClick,
  openLinksInNewTab = true,
  urlDisplayLength = 30
}) => {
  // Combined regex to find all matches in order
  const combinedRegex = /(#\w+|@\w+|https?:\/\/[^\s]+)/g;
  const elements: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = combinedRegex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      elements.push(text.slice(lastIndex, match.index));
    }

    const matchText = match[0];
    const key = `${match.index}-${matchText}`;

    if (matchText.startsWith('#')) {
      // Hashtag
      const hashtag = matchText.slice(1); // Remove the #
      elements.push(
        <span
          key={key}
          className={hashtagClassName}
          onClick={() => onHashtagClick?.(hashtag)}
          role={onHashtagClick ? 'button' : undefined}
          tabIndex={onHashtagClick ? 0 : undefined}
          onKeyDown={(e) => {
            if (onHashtagClick && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              onHashtagClick(hashtag);
            }
          }}
        >
          {matchText}
        </span>
      );
    } else if (matchText.startsWith('@')) {
      // Mention
      const username = matchText.slice(1); // Remove the @
      elements.push(
        <span
          key={key}
          className={mentionClassName}
          onClick={() => onMentionClick?.(username)}
          role={onMentionClick ? 'button' : undefined}
          tabIndex={onMentionClick ? 0 : undefined}
          onKeyDown={(e) => {
            if (onMentionClick && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              onMentionClick(username);
            }
          }}
        >
          {matchText}
        </span>
      );
    } else if (matchText.startsWith('http')) {
      // URL
      const displayUrl = matchText.length > urlDisplayLength 
        ? `${matchText.slice(0, urlDisplayLength)}...` 
        : matchText;

      const handleLinkClick = (e: React.MouseEvent) => {
        if (onLinkClick) {
          e.preventDefault();
          onLinkClick(matchText);
        }
      };

      elements.push(
        <a
          key={key}
          href={matchText}
          target={openLinksInNewTab ? '_blank' : undefined}
          rel={openLinksInNewTab ? 'noopener noreferrer' : undefined}
          className={linkClassName}
          onClick={handleLinkClick}
        >
          {displayUrl}
        </a>
      );
    }

    lastIndex = match.index + matchText.length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    elements.push(text.slice(lastIndex));
  }

  return <>{elements.length > 0 ? elements : text}</>;
};

export default MessageFormatter;